import React, { useState } from 'react';

import { Button, Grid, IconButton, makeStyles, MenuItem, Select, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Link } from 'react-router-dom';

import CategorySelect from './CategorySelect';
import PrivacySelect from './PrivacySelect';
import ResourceUpdateNotification from './ResourceUpdateNotification';
import SearchInput from './SearchInput';
import TypeSelect from './TypeSelect';

const useStyle = makeStyles((theme) => {
  return {
    buttonAddResource: {
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        width: '150px',
      },
    },
    buttonCreatorMode: {
      marginBottom: '8px',
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        marginBottom: '0',
        marginRight: '8px',
        width: '150px',
      },
    },
    filterBy: {
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        width: '280px',
      },
    },
    filterByContainer: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'flex-end',
    },
    link: {
      color: theme.palette.primary.main,
      fontSize: '14px',
      fontWeight: '600',
      textDecoration: 'none',
    },
    title: {
      lineHeight: '100%',
      marginRight: '12px',
    },
    titleBorder: {
      borderRight: '2px solid #999',
      height: '24px',
      marginRight: '12px',
    },
    titleContainer: {
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      marginBottom: '16px',
    },
  };
});

const types = {
  '3D Models': '3d',
  Documents: 'document',
  Images: 'image',
  Studies: 'dcm',
  Videos: 'video',
};

const categories = {
  'Ambra Studies': 'sharedStudy',
  Educational: 'educational',
  'Self Help Materials': 'self-help',
  Supplementary: 'supplementary',
};

const filterByRenderValue = (value) => {
  if (!value) return <span style={{ color: '#999999' }}>Filter by</span>;

  const mappedValue = {
    selfHelpCategory: 'Self Help Category',
    tags: 'Tags',
    title: 'Title',
  };

  return mappedValue?.[value] || value;
};

const ResourcesHeader = ({
  onCriteriaChange,
  onAddResource,
  isCreatorMode,
  isSuperAdmin,
  onCreatorMode,
  onRefresh,
  newResources$,
  disableType,
  disableCategory,
  disableAddResource,
  disablePrivacy,
  shouldHideSelfHelpMaterials,
  disableTemplateView,
}) => {
  const classes = useStyle();

  const [selectedCategory, setSelectedCategory] = useState(['Educational', 'Supplementary', 'Self Help Materials']);
  const [selectedType, setSelectedType] = useState(['3D Models', 'Images', 'Studies', 'Videos', 'Documents']);
  const [selectedPrivacy, setSelectedPrivacy] = useState('');
  const [keyword, setKeyword] = useState('');
  const [filterBy, setFilterBy] = useState('');
  const getTypeValue = (value) => {
    return types[value];
  };

  const getCategoryValue = (value) => {
    return categories[value];
  };
  const handleCategoryChange = (event) => {
    const selectedCategories = event.target.value;
    setSelectedCategory(selectedCategories);
    const categoriesString = selectedCategories.map((cat) => getCategoryValue(cat)).join(',');
    if (categoriesString.includes('supplementary')) {
      const resourceTypes = [...selectedType];
      if (!resourceTypes.includes('Videos')) {
        resourceTypes.push('Videos');
        setSelectedType(resourceTypes);
      }
      const mappedTypes = resourceTypes.map((type) => getTypeValue(type)).join(',');

      onCriteriaChange({ category: categoriesString || 'no-category', page: 0, type: mappedTypes || 'no-type' });
    } else {
      onCriteriaChange({ category: categoriesString || 'no-category', page: 0 });
    }
  };

  const handleSearchChange = (event) => {
    setKeyword(event.target.value);
  };

  const handleSearchClick = () => {
    onCriteriaChange({ filterBy, keyword, page: 0 });
  };

  const handleUpdateFilterBy = (value) => {
    onCriteriaChange({ filterBy: value, keyword, page: 0 });
  };

  const handleFilterByClear = () => {
    setFilterBy('');
    handleUpdateFilterBy('');
  };

  const handleFilterByChange = (e) => {
    setFilterBy(e.target.value);
    handleUpdateFilterBy(e.target.value);
  };

  const handleTypeChange = (event) => {
    const selectedTypes = event.target.value;
    setSelectedType(selectedTypes);
    const type = selectedTypes.map((_type) => getTypeValue(_type)).join(',');
    onCriteriaChange({ page: 0, type });
  };

  const handlePrivacyChange = (event) => {
    setSelectedPrivacy(event.target.value);
    onCriteriaChange({ page: 0, privacy: event.target.value });
  };

  const handleRefresh = () => {
    if (onRefresh) onRefresh();
    setKeyword('');
    setSelectedCategory(['Educational', 'Supplementary']);
    setSelectedType(['3D Models', 'Images', 'Studies', 'Videos']);
  };

  return (
    <Grid container className="text-left">
      <Grid item xs={12}>
        <div className={classes.titleContainer}>
          <Typography className={`page-header mb-0 ${classes.title}`}>Resource Library</Typography>
          {disableTemplateView ? null : (
            <>
              <div className={classes.titleBorder} />
              <Link className={classes.link} to="/collections">
                View Collections
              </Link>
            </>
          )}
        </div>
      </Grid>
      <Grid item xs={12}>
        <Grid container justify="space-between">
          <Grid item xs={12} sm={6} md={7} lg={9} container>
            {!disableCategory && (
              <CategorySelect selectedCategory={selectedCategory} isSuperAdmin={isSuperAdmin} onCategoryChange={handleCategoryChange} categoryList={categories} />
            )}
            {!disableType && <TypeSelect selectedType={selectedType} onTypeChange={handleTypeChange} typeList={types} />}

            {!disablePrivacy && <PrivacySelect selectedPrivacy={selectedPrivacy} onPrivacyChange={handlePrivacyChange} />}
          </Grid>
          <Grid item xs={12} sm={6} md={5} lg={3} container justify="flex-end">
            {isCreatorMode && (
              <div className={classes.buttonCreatorMode}>
                <Button fullWidth variant="outlined" color="primary" onClick={onCreatorMode}>
                  Creator Mode
                </Button>
              </div>
            )}

            <div className={classes.buttonAddResource}>
              {!disableAddResource && (
                <Button fullWidth variant="contained" color="primary" onClick={onAddResource}>
                  Add Resource
                </Button>
              )}
            </div>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container justify="space-between">
          <Grid item xs={12} sm={6}>
            <SearchInput onSearchChange={handleSearchChange} onSearchClick={handleSearchClick} keyword={keyword} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <div className={classes.filterByContainer}>
              <Select
                displayEmpty
                renderValue={filterByRenderValue}
                className={`bold-icon no-background-focus ${classes.filterBy}`}
                name="selectedFilter"
                onChange={handleFilterByChange}
                value={filterBy}>
                <MenuItem value="title">Title</MenuItem>
                {!shouldHideSelfHelpMaterials && <MenuItem value="selfHelpCategory">Self Help Category</MenuItem>}
                <MenuItem value="tags">Tags</MenuItem>
              </Select>

              {!!filterBy && (
                <IconButton>
                  <CloseIcon onClick={handleFilterByClear} />
                </IconButton>
              )}
            </div>
          </Grid>
        </Grid>
      </Grid>
      <ResourceUpdateNotification newResources$={newResources$} onRefresh={handleRefresh} />
    </Grid>
  );
};

export default ResourcesHeader;
